/**
 * Single-source-of-truth state for Live Ball. Custom pub-sub — listeners get
 * notified on every committed mutation.
 */
import { DEFAULT_GAME_CONFIG } from '../config';
import type {
  BallVariant,
  GameConfig,
  GamePhase,
  GameRound,
} from '../types/game';
import { BetCollection } from './betCollection';

export interface GameStateSnapshot {
  phase: GamePhase;
  balance: number;
  selectedChipIndex: number;
  bets: BetCollection;
  /** Stored bets that ULANG (repeat) re-applies. Updated on each PASANG. */
  lastConfirmedBets: BetCollection;
  config: GameConfig;
  variant: BallVariant;
  currentRound: GameRound | null;
  lastWinLose: number;
  countdownSec: number;
  errorMessage: string | null;
}

type Listener = (snapshot: GameStateSnapshot, prev: GameStateSnapshot) => void;

/**
 * Wallet figure shown to the player in the top-bar pill.
 *
 * Pre-result (betting, draw, animation): we show the pre-deducted view
 * `balance - bets.total` so chips clicked on the table immediately tick the
 * wallet down, and BATAL ticks it back up.
 *
 * Post-result (result reveal onwards): the server's authoritative balance
 * already accounts for the round, so we switch to showing it directly.
 * `state.bets` is still rendered as chips until the next StartGame, so the
 * player can see what they bet on while the result is on screen.
 */
export function displayedBalance(s: GameStateSnapshot): number {
  const preResult =
    s.phase === 'betting' ||
    s.phase === 'bettingClosed' ||
    s.phase === 'waitingResult' ||
    s.phase === 'drawing';
  return preResult ? s.balance - s.bets.total : s.balance;
}

export class GameState {
  private _phase: GamePhase = 'idle';
  private _balance = 0;
  private _selectedChipIndex = 0;
  private _bets: BetCollection = BetCollection.empty();
  private _lastConfirmedBets: BetCollection = BetCollection.empty();
  private _config: GameConfig = { ...DEFAULT_GAME_CONFIG };
  private _variant: BallVariant;
  private _currentRound: GameRound | null = null;
  private _lastWinLose = 0;
  private _countdownSec = 0;
  private _errorMessage: string | null = null;

  private listeners: Set<Listener> = new Set();

  constructor(variant: BallVariant) {
    this._variant = variant;
  }

  snapshot(): GameStateSnapshot {
    return {
      phase: this._phase,
      balance: this._balance,
      selectedChipIndex: this._selectedChipIndex,
      bets: this._bets,
      lastConfirmedBets: this._lastConfirmedBets,
      config: this._config,
      variant: this._variant,
      currentRound: this._currentRound,
      lastWinLose: this._lastWinLose,
      countdownSec: this._countdownSec,
      errorMessage: this._errorMessage,
    };
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get availableBalance(): number {
    return this._balance - this._bets.total;
  }
  get phase(): GamePhase { return this._phase; }
  get bets(): BetCollection { return this._bets; }
  get lastConfirmedBets(): BetCollection { return this._lastConfirmedBets; }
  get config(): GameConfig { return this._config; }
  get variant(): BallVariant { return this._variant; }
  get balance(): number { return this._balance; }
  get selectedChipIndex(): number { return this._selectedChipIndex; }
  get selectedChipValue(): number {
    return this._config.chipValues[this._selectedChipIndex] ?? this._config.chipValues[0];
  }
  get countdownSec(): number { return this._countdownSec; }
  get currentRound(): GameRound | null { return this._currentRound; }

  // ---------- Mutators ----------

  setPhase(p: GamePhase): void {
    if (p === this._phase) return;
    this.commit((s) => { s._phase = p; });
  }
  setBalance(b: number): void {
    if (b === this._balance) return;
    this.commit((s) => { s._balance = b; });
  }
  setConfig(c: GameConfig): void {
    this.commit((s) => {
      s._config = { ...c };
      if (s._selectedChipIndex >= c.chipValues.length) s._selectedChipIndex = 0;
    });
  }
  selectChip(i: number): void {
    const max = this._config.chipValues.length;
    if (i < 0 || i >= max || i === this._selectedChipIndex) return;
    this.commit((s) => { s._selectedChipIndex = i; });
  }
  setCountdown(sec: number): void {
    if (sec === this._countdownSec) return;
    this.commit((s) => { s._countdownSec = sec; });
  }

  /** Returns true if the bet was accepted; false if it busts limits/balance. */
  placeBet(key: string, amount: number): boolean {
    if (this._phase !== 'betting') return false;
    if (amount <= 0) return false;
    if (amount > this.availableBalance) return false;
    const cur = this._bets.amountFor(key);
    if (cur + amount > this._config.maxBet) return false;
    this.commit((s) => {
      const next = s._bets.copy();
      next.add(key, amount);
      s._bets = next;
    });
    return true;
  }

  clearBets(): void {
    if (this._bets.isEmpty) return;
    this.commit((s) => { s._bets = BetCollection.empty(); });
  }

  /** Repeat last confirmed round's bets (ULANG). Returns true if any applied. */
  repeatBets(): boolean {
    if (this._phase !== 'betting') return false;
    if (this._lastConfirmedBets.isEmpty) return false;
    const next = BetCollection.empty();
    let totalAdded = 0;
    for (const [key, amt] of this._lastConfirmedBets.asMap()) {
      // Bound check against current free balance.
      if (totalAdded + amt > this.availableBalance) break;
      next.add(key, amt);
      totalAdded += amt;
    }
    if (next.isEmpty) return false;
    this.commit((s) => { s._bets = next; });
    return true;
  }

  /** Called after a successful PASANG — locks current bets for ULANG. */
  storeAsLastConfirmed(): void {
    this.commit((s) => { s._lastConfirmedBets = s._bets.copy(); });
  }

  applyRoundResult(round: GameRound): void {
    this.commit((s) => {
      s._currentRound = round;
      s._lastWinLose = round.totalWinLose;
    });
  }

  resetForNewRound(): void {
    this.commit((s) => {
      s._bets = BetCollection.empty();
      s._currentRound = null;
      s._lastWinLose = 0;
      s._errorMessage = null;
    });
  }

  setError(message: string | null): void {
    if (message === this._errorMessage) return;
    this.commit((s) => {
      s._errorMessage = message;
      if (message !== null) s._phase = 'error';
    });
  }

  private commit(mutator: (s: GameState) => void): void {
    const prev = this.snapshot();
    mutator(this);
    const next = this.snapshot();
    for (const l of this.listeners) {
      try {
        l(next, prev);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[GameState listener]', e);
      }
    }
  }
}
